// =============================================================================
// src/features/reputation/reputation-ticker.ts
//
// Ticker financeiro ao vivo (feed de menções + métricas) com pausa.
// Porte FIEL de assets/reputation.js: tickerItemHTML 386-400, buildTicker 401-421,
//   o listener do tickerToggle 422-429 e os icones TICKER_ICON 382-385.
// Preserva: 45 px/s, duracao = max(20, round(oneSet/45)), duplicacao do set para
//   loop infinito, ramo estatico em prefers-reduced-motion e os aria-* do botao.
//
// SRP: so o ticker. Recebe reduceMotion por argumento (DI; sem matchMedia global).
// =============================================================================

import { REPUTATION_TICKER_DATA, REPUTATION_TICKER_PXPS } from './reputation-data';
import type { ReputationTickerItem } from './reputation-data';

/** Icones de play/pause do botao do ticker — assets/reputation.js 382-385. */
const TICKER_ICON = {
  pause: '<i class="ti ti-player-pause-filled rep-ico sm" aria-hidden="true"></i>',
  play: '<i class="ti ti-player-play-filled rep-ico sm" aria-hidden="true"></i>',
} as const;

/** Monta o HTML de um item do ticker — porte FIEL de `tickerItemHTML` (reputation.js 386-400). */
function tickerItemHTML(it: ReputationTickerItem): string {
  if (it.kind === 'metric') {
    const arrow = (it.dir === 'up-bad' || it.dir === 'up-good')
      ? '<i class="ti ti-trending-up rep-ico sm" aria-hidden="true"></i>'
      : (it.dir === 'down-bad' ? '<i class="ti ti-trending-down rep-ico sm" aria-hidden="true"></i>' : '');
    return '<span class="rep-ticker-item"><span class="rep-ticker-metric">' +
      `<span class="lbl">${it.label}</span>` +
      `<span class="val ${it.dir}">${arrow}${it.value}</span></span></span>`;
  }
  return '<span class="rep-ticker-item">' +
    `<span class="rep-ticker-pip2 ${it.sent}" aria-hidden="true"></span>` +
    `<span class="rep-ticker-src">${it.src}</span>` +
    `<span class="rep-ticker-txt">${it.text}</span>` +
    `<span class="rep-ticker-time">${it.time}</span></span>`;
}

/**
 * (Re)constroi o ticker — porte FIEL de `buildTicker` (reputation.js 401-421).
 * Em reduce-motion vira lista estatica; senao duplica o set e calcula a duracao
 * a partir da largura medida (so quando o track esta visivel: offsetParent != null).
 */
export function buildReputationTicker(reduceMotion: boolean): void {
  const ticker = document.getElementById('repTicker');
  const tickerTrack = document.getElementById('repTickerTrack');
  if (!ticker || !tickerTrack) return;
  const set = REPUTATION_TICKER_DATA.map(tickerItemHTML).join('');
  if (reduceMotion) {
    tickerTrack.classList.remove('is-animated');
    tickerTrack.innerHTML = set;
    ticker.classList.add('is-static');
    return;
  }
  ticker.classList.remove('is-static');
  tickerTrack.classList.remove('is-animated');
  tickerTrack.innerHTML = set + set;
  applyTickerDuration(ticker, tickerTrack);
}

/** Mede um set e seta --rep-ticker-dur quando visivel — extrai reputation.js 413-420. */
function applyTickerDuration(ticker: HTMLElement, tickerTrack: HTMLElement): void {
  if (tickerTrack.offsetParent === null) return;
  const oneSet = tickerTrack.scrollWidth / 2;
  if (oneSet <= 0) return;
  const dur = Math.max(20, Math.round(oneSet / REPUTATION_TICKER_PXPS));
  ticker.style.setProperty('--rep-ticker-dur', `${dur}s`);
  tickerTrack.classList.add('is-animated');
}

/** Liga o botao de pausar/retomar o feed — porte FIEL do listener (reputation.js 422-429). */
export function bindReputationTickerToggle(): void {
  const ticker = document.getElementById('repTicker');
  const tickerToggle = document.getElementById('repTickerToggle');
  if (!ticker || !tickerToggle) return;
  tickerToggle.addEventListener('click', () => {
    const paused = ticker.classList.toggle('is-paused');
    tickerToggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    tickerToggle.setAttribute('aria-label', paused ? 'Retomar feed' : 'Pausar feed');
    tickerToggle.innerHTML = paused ? TICKER_ICON.play : TICKER_ICON.pause;
  });
}
